<template>
  <div
    v-if="open"
    :class="{ 'modal-open': open, 'product': props.isProduct }"
    class="modal--component fixed inset-0 flex items-center justify-center z-[1200] px-4"
  >
    <div v-if="loading" class="loading-overlay">
      <Spinner :color="'white'"/>
    </div>
    <div
      class="modal--component-overlay fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 backdrop-blur-sm z-[100]"
      @click="closeModal()"
    />
    <div
      :class="{ 'html overflow-hidden': isHtml, 'px-6 py-6': !props.isProduct, 'product': props.isProduct, 'fit-content-height': fitContentHeight, 'fit-content-width': fitContentWidth }"
      class="modal--component-container bg-white w-full lg:w-96 elevation-5 text-left z-[101] overflow-hidden"
    >
      <slot name="header">
        <div class="flex z-20 h-[10%]">
          <button @click="closeModal()" class="close-button mt-6 text-black font-semibold py-2 px-4 rounded">
            <Icon icon-name="close_200" size="46"/>
          </button>
        </div>
      </slot>

        <div id="modal-body" class="modal--component-body h-[90%] overflow-scroll">
          <slot name="body">
            <h2 class="text-xl font-bold mb-4">{{ props.modalHeadline }}</h2>
            <p v-if="!isHtml" class="slightly-smaller">{{ props.modalBody }}</p>
            <div v-else class="slightly-smaller" v-html="props.modalBody" />
          </slot>
        </div>

        <slot name="footer">
          <div class="!overflow-y-auto">
            <Button
              v-if="props.modalButtonText"
              :classes="'btn btn--fullw smaller !py-[0.65rem] action'"
              :button-text="props.modalButtonText"
              button-style="success"
              rounded
              @event:button-press="props.modalButtonAction"
            />
         </div>
        </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Icon from '@/components/atoms/Icon/Icon.vue'
  import Button from '@/components/atoms/Button/Button.vue'
  import Spinner from '@/components/atoms/Spinner/Spinner.vue'

  const emit = defineEmits(['modal:close', 'modal:open'])
  const props = defineProps({
    open: { type: Boolean, default: false },
    modalHeadline: { type: String, required: false },
    modalBody: { type: String, required: false },
    modalButtonText: { type: String, default: '' },
    modalButtonAction: { type: Function },
    isHtml: { type: Boolean, default: false },
    isProduct: { type: Boolean, default: false },
    fitContentHeight: { type: Boolean, default: false },
    fitContentWidth: { type: Boolean, default: false },
    loading: { type: Boolean, default: false }
  })

  const closeModal = () => {
    emit('modal:close', false)
  }
</script>

<style scoped lang="scss">
  @import '@/styles/_global';

  .modal {
    &--component {
      &-overlay {
        transition: opacity 0.2s;
      }

      &-container {
        position: absolute;
        opacity: 1;
        border-radius: 25px;
        width: 100dvw;
        height: 100dvh;
        border-radius: 0;

        @media screen and (min-width: 768px) {
          max-width: 640px;
          height: 90dvh;
          border-radius: 25px;
        }
        
        &.fit-content-height {
          height: fit-content;
          max-height: 90dvh;
          .modal--component-body {
            max-height: 60dvh !important;
            overflow: auto !important;
          }
        }
        
        &.fit-content-width {
          width: fit-content;
        }

        &.product {
          @media screen and (min-width: 768px) {
            max-width: 100dvw;
            height: 100dvh;
            border-radius: 0;
          }

          @media screen and (min-width: 1280px) {
            height: 90dvh;
            border-radius: 25px;
            max-width: 1200px !important;
          }
        }

        &.html {
          padding-left: 0;
          padding-right: 0;

          > .modal--component-body {
            height: calc(100% - 35px); // to not go from edge to edge of screen
            overflow-y: scroll;
            overflow-x: clip;
            padding-left: 35px;
            padding-right: 35px;
            padding-bottom: 10px;
          }

          > .modal--component-footer {
            padding-left: 30px;
            padding-right: 35px;
          }
        }
      }

      &-body {
        border-radius: 25px;
        overflow: hidden;
        overflow-y: auto;

        @media (min-width: 1280px) {
          @import '@/styles/components/_scrollbar';
        }

        &::-webkit-scrollbar-track {
          margin: 50px 0 25px 0;
        }
      }
    }
  }

  .close-button {
    position: absolute;
    top: -15px;
    right: 0;
  }
  
.loading-overlay {
  position: absolute;
  display: grid;
  justify-items: center;
  align-items: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 10000;
}
  </style>
